/**
 * Obsah webu – správa veřejného obsahu webu z interní aplikace.
 * Ukládá do web_settings (texty, kontakty, sociální sítě, rider)
 * a do web_members (členové kapely).
 */
import { supabase } from '../lib/supabase.js';
import { compressImage } from '../lib/image-compress.js';

const BUCKET = 'web-media';

function esc(s) {
    if (s == null) return '';
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function publicUrl(path) {
    if (!path) return '';
    return supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
}

async function getWebSettings() {
    const { data } = await supabase.from('web_settings').select('key, value');
    return Object.fromEntries((data || []).map(s => [s.key, s.value]));
}

async function saveWebSetting(key, value, statusEl) {
    const { error } = await supabase.from('web_settings')
        .upsert({ key, value, updated_at: new Date().toISOString() });
    if (statusEl) {
        if (error) {
            statusEl.textContent = 'Chyba: ' + error.message;
            statusEl.dataset.type = 'err';
        } else {
            statusEl.textContent = 'Uloženo ✓';
            statusEl.dataset.type = 'ok';
        }
        statusEl.style.opacity = '1';
        clearTimeout(saveWebSetting._t);
        saveWebSetting._t = setTimeout(() => {
            statusEl.textContent = 'Změny se ukládají automaticky';
            statusEl.dataset.type = 'idle';
            statusEl.style.opacity = '0.5';
        }, 2000);
    }
}

async function getMembers() {
    const { data } = await supabase.from('web_members').select('*').order('position');
    return data || [];
}

function webField(label, key, settings, type = 'text', hint = '') {
    const val = settings[key] ?? '';
    return `
        <div class="settings-row">
            <label>${esc(label)}${hint ? ` <span class="unit">${esc(hint)}</span>` : ''}</label>
            ${type === 'textarea'
                ? `<textarea data-websetting="${esc(key)}" rows="3" style="font-family:inherit;font-size:13px;padding:5px 8px;border:1px solid var(--border);border-radius:4px;resize:vertical;">${esc(val)}</textarea>`
                : `<input type="${type}" data-websetting="${esc(key)}" value="${esc(val)}">`}
        </div>`;
}

function memberRow(m) {
    const photo = m.photo_path ? publicUrl(m.photo_path) : '';
    return `
        <tr data-member-id="${esc(m.id)}">
            <td style="text-align:center; padding:6px;">
                ${photo
                    ? `<img src="${esc(photo)}" style="width:44px;height:44px;border-radius:50%;object-fit:cover;">`
                    : `<div style="width:44px;height:44px;border-radius:50%;background:var(--surface-alt);margin:auto;display:flex;align-items:center;justify-content:center;font-size:18px;color:var(--text-muted);">👤</div>`}
            </td>
            <td><strong>${esc(m.name)}</strong></td>
            <td>${esc(m.instrument || '—')}</td>
            <td style="max-width:260px;color:var(--text-muted);font-size:12px;">${esc((m.bio || '').slice(0, 80))}${(m.bio || '').length > 80 ? '…' : ''}</td>
            <td style="white-space:nowrap;">
                <button class="mini-btn" data-member-edit="${esc(m.id)}">Upravit</button>
                <button class="mini-btn" data-member-del="${esc(m.id)}" data-member-name="${esc(m.name)}">Smazat</button>
            </td>
        </tr>`;
}

export async function renderWebObsah(container) {
    container.innerHTML = '<div class="state-banner">Načítám…</div>';

    const [settings, members] = await Promise.all([getWebSettings(), getMembers()]);
    const heroUrl = publicUrl(settings.hero_photo || '');

    container.innerHTML = `
        <div class="filters" style="align-items:center;">
            <h2 style="font-size:18px; margin-right:auto;">Obsah webu</h2>
            <span id="web-save-status" data-type="idle"
                  style="font-size:13px;padding:6px 12px;border-radius:999px;transition:opacity 0.3s;opacity:0.5;">
                Změny se ukládají automaticky
            </span>
        </div>

        <div class="settings-grid">

            <div class="settings-card">
                <h3>Základní informace</h3>
                ${webField('Název kapely (web)', 'band_name', settings)}
                ${webField('Úvodní text (homepage)', 'intro_text', settings, 'textarea')}
                ${webField('O nás — text', 'about_text', settings, 'textarea')}
                ${webField('Reference — úvod', 'references_intro', settings, 'textarea')}
                ${webField('Reference — místa / text', 'references_places', settings, 'textarea')}
            </div>

            <div class="settings-card">
                <h3>Kontakt</h3>
                ${webField('Jméno (kontaktní osoba)', 'contact_name', settings)}
                ${webField('E-mail', 'contact_email', settings, 'email')}
                ${webField('Telefon', 'contact_phone', settings, 'tel')}
            </div>

            <div class="settings-card">
                <h3>Sociální sítě</h3>
                ${webField('Facebook URL', 'facebook_url', settings, 'url')}
                ${webField('Instagram URL', 'instagram_url', settings, 'url')}
                ${webField('YouTube URL', 'youtube_url', settings, 'url')}
            </div>

            <div class="settings-card">
                <h3>Technické požadavky / Rider</h3>
                ${webField('Rider — text', 'rider_text', settings, 'textarea')}
                ${webField('Technické požadavky', 'tech_requirements', settings, 'textarea')}
                ${webField('Zvukové zapojení', 'sound_connection', settings)}
                ${webField('Poznámky ke zvuku', 'sound_notes', settings, 'textarea')}
            </div>

            <div class="settings-card">
                <h3>Hlavní foto (hero)</h3>
                <p class="hint">Velký obrázek v záhlaví úvodní stránky webu.</p>
                ${heroUrl
                    ? `<div style="margin-bottom:10px;"><img src="${esc(heroUrl)}" alt="hero" style="width:100%;max-height:180px;object-fit:cover;border-radius:8px;border:1px solid var(--border);"></div>`
                    : `<div style="padding:20px;text-align:center;color:var(--text-muted);border:2px dashed var(--border);border-radius:8px;margin-bottom:10px;">Zatím žádné hlavní foto</div>`}
                <label class="btn btn-sm" style="display:inline-block;cursor:pointer;">
                    ${heroUrl ? 'Změnit foto' : '+ Nahrát foto'}
                    <input type="file" id="hero-upload" accept="image/*" style="display:none;">
                </label>
                <span id="hero-status" style="font-size:12px;color:var(--text-muted);margin-left:10px;"></span>
            </div>

        </div>

        <div class="settings-card wide" style="margin-top:16px;">
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
                <h3 style="margin:0;">Členové kapely</h3>
                <button class="btn btn-sm btn-primary" id="add-member-btn">+ Přidat člena</button>
            </div>
            <p class="hint">Tito členové se zobrazují na veřejném webu v sekci „O nás / Kapela". Pořadí odpovídá hodnotě Pozice.</p>
            ${members.length === 0
                ? '<div class="state-banner" style="margin:0;">Zatím žádní členové. Přidej prvního tlačítkem výše.</div>'
                : `<table class="settings-musicians">
                        <thead><tr>
                            <th style="width:54px;"></th>
                            <th>Jméno</th>
                            <th>Nástroj</th>
                            <th>Bio (zkráceno)</th>
                            <th>Akce</th>
                        </tr></thead>
                        <tbody>${members.map(memberRow).join('')}</tbody>
                    </table>`}
        </div>

        <div class="footer-note">
            Veškerý obsah se ukládá přímo do Supabase — změny se projeví na veřejném webu
            <strong>okamžitě</strong> po uložení (nebo při přijetí dalšího požadavku, záleží na cachování webu).
        </div>
    `;

    const statusEl = container.querySelector('#web-save-status');

    // Auto-save pro web_settings vstupy
    container.querySelectorAll('[data-websetting]').forEach(el => {
        const save = () => saveWebSetting(el.dataset.websetting, el.value.trim(), statusEl);
        el.addEventListener('change', save);
        if (el.tagName === 'TEXTAREA') el.addEventListener('blur', save);
    });

    // Hero foto upload
    container.querySelector('#hero-upload').addEventListener('change', async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const statusSpan = container.querySelector('#hero-status');
        statusSpan.textContent = 'Nahrávám…';
        try {
            const compressed = await compressImage(file, { maxWidth: 2560, quality: 0.88 });
            const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
            const path = `hero/hero.${ext}`;
            const { error } = await supabase.storage.from(BUCKET)
                .upload(path, compressed, { contentType: compressed.type || 'image/jpeg', upsert: true });
            if (error) throw error;
            await saveWebSetting('hero_photo', path, statusEl);
            statusSpan.textContent = '';
            renderWebObsah(container);
        } catch (ex) {
            statusSpan.textContent = 'Chyba: ' + (ex.message || ex);
        }
    });

    // Přidat člena
    container.querySelector('#add-member-btn').addEventListener('click', () => {
        openMemberModal({ member: null, onSaved: () => renderWebObsah(container) });
    });

    // Upravit / Smazat člena
    container.querySelectorAll('[data-member-edit]').forEach(btn => {
        btn.addEventListener('click', () => {
            const m = members.find(x => x.id === btn.dataset.memberEdit);
            if (m) openMemberModal({ member: m, onSaved: () => renderWebObsah(container) });
        });
    });
    container.querySelectorAll('[data-member-del]').forEach(btn => {
        btn.addEventListener('click', async () => {
            if (!confirm(`Smazat člena „${btn.dataset.memberName}"?`)) return;
            const { error } = await supabase.from('web_members').delete().eq('id', btn.dataset.memberDel || btn.closest('tr').dataset.memberId);
            if (error) { alert('Chyba: ' + error.message); return; }
            renderWebObsah(container);
        });
    });
}

function openMemberModal({ member, onSaved }) {
    let m = document.getElementById('member-modal');
    if (m) m.remove();
    m = document.createElement('div');
    m.id = 'member-modal';
    m.className = 'modal-overlay open';

    const photoUrl = member?.photo_path ? publicUrl(member.photo_path) : '';

    m.innerHTML = `
        <div class="modal-window" style="max-width:500px;">
            <div class="modal-head">
                <h2>${member ? 'Upravit člena' : 'Přidat člena kapely'}</h2>
                <button class="modal-close" type="button">×</button>
            </div>
            <form id="member-form">
                <div class="modal-body">
                    <div class="spec-section">
                        <div class="spec-row">
                            <label>Jméno</label>
                            <input type="text" name="name" required value="${esc(member?.name || '')}"
                                   style="min-width:220px;text-align:left;">
                        </div>
                        <div class="spec-row">
                            <label>Nástroj / role</label>
                            <input type="text" name="instrument" value="${esc(member?.instrument || '')}"
                                   placeholder="kytara, zpěv, bicí…" style="min-width:220px;text-align:left;">
                        </div>
                        <div class="spec-row" style="grid-template-columns:1fr;">
                            <label>Bio / popis</label>
                        </div>
                        <div class="spec-row" style="grid-template-columns:1fr;">
                            <textarea name="bio" rows="4" placeholder="Krátký popis člena pro veřejný web…"
                                      style="width:100%;padding:6px 10px;border:1px solid var(--border);border-radius:4px;font-family:inherit;font-size:13px;">${esc(member?.bio || '')}</textarea>
                        </div>
                        <div class="spec-row">
                            <label>Pozice <span class="unit">pořadí na webu</span></label>
                            <input type="number" name="position" value="${member?.position ?? 0}" min="0">
                        </div>
                        <div class="spec-row">
                            <label>Foto</label>
                            <div>
                                ${photoUrl
                                    ? `<img src="${esc(photoUrl)}" style="width:60px;height:60px;border-radius:50%;object-fit:cover;display:block;margin-bottom:6px;">`
                                    : ''}
                                <label class="btn btn-sm" style="display:inline-block;cursor:pointer;">
                                    ${photoUrl ? 'Změnit' : 'Nahrát foto'}
                                    <input type="file" id="member-photo-file" accept="image/*" style="display:none;">
                                </label>
                                <span id="member-photo-status" style="font-size:12px;color:var(--text-muted);margin-left:8px;"></span>
                                <input type="hidden" name="photo_path" value="${esc(member?.photo_path || '')}">
                            </div>
                        </div>
                    </div>
                    <p id="member-error" style="color:var(--accent);font-size:13px;margin-top:10px;display:none;"></p>
                </div>
                <div class="modal-foot">
                    <button class="btn" type="button" id="member-cancel">Zrušit</button>
                    <button class="btn btn-primary" type="submit">Uložit</button>
                </div>
            </form>
        </div>
    `;
    document.body.appendChild(m);

    const close = () => m.remove();
    m.querySelector('.modal-close').addEventListener('click', close);
    m.querySelector('#member-cancel').addEventListener('click', close);
    m.addEventListener('click', (e) => { if (e.target === m) close(); });

    // Foto upload
    m.querySelector('#member-photo-file').addEventListener('change', async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const statusSpan = m.querySelector('#member-photo-status');
        statusSpan.textContent = 'Nahrávám…';
        try {
            const compressed = await compressImage(file, { maxWidth: 800, quality: 0.88 });
            const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
            const fname = `members/${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`;
            const { error } = await supabase.storage.from(BUCKET)
                .upload(fname, compressed, { contentType: compressed.type || 'image/jpeg', upsert: true });
            if (error) throw error;
            m.querySelector('[name="photo_path"]').value = fname;
            statusSpan.textContent = 'Nahráno ✓';
        } catch (ex) {
            statusSpan.textContent = 'Chyba: ' + (ex.message || ex);
        }
    });

    m.querySelector('#member-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const errEl = m.querySelector('#member-error');
        errEl.style.display = 'none';
        const fd = new FormData(e.target);

        const payload = {
            name:       fd.get('name').trim(),
            instrument: fd.get('instrument').trim() || null,
            bio:        fd.get('bio').trim() || null,
            position:   parseInt(fd.get('position'), 10) || 0,
            photo_path: fd.get('photo_path') || null
        };

        let error;
        if (member) {
            ({ error } = await supabase.from('web_members').update(payload).eq('id', member.id));
        } else {
            ({ error } = await supabase.from('web_members').insert(payload));
        }

        if (error) {
            errEl.textContent = 'Chyba: ' + error.message;
            errEl.style.display = 'block';
            return;
        }
        close();
        onSaved?.();
    });
}
